/**
 * Sentry error tracking initialization.
 *
 * Must be imported and called BEFORE installGlobalErrorTracking() in every
 * entry point.  The existing errorTrackingSink looks for window.Sentry and
 * routes captured errors there automatically when VITE_ERROR_TRACKING_SINKS
 * includes "sentry".
 */

import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { getBrand } from '@/lib/branding';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const echidnaDsn = import.meta.env.VITE_SENTRY_DSN_ECHIDNA as string | undefined;

/**
 * Auth error codes that mean "user typed something wrong" — wrong
 * password, unconfirmed email, weak password during signup, etc.
 * AuthContext logs these at info level today, but if some other path
 * surfaces them at error level (legacy code, new feature, etc.) this
 * filter still drops them at the SDK boundary.
 *
 * Keep this list in sync with EXPECTED_AUTH_ERROR_CODES in
 * src/contexts/AuthContext.tsx.
 */
const EXPECTED_AUTH_CODES = [
  'invalid_credentials',
  'email_not_confirmed',
  'user_already_exists',
  'user_already_registered',
  'weak_password',
  'over_email_send_rate_limit',
  'over_request_rate_limit',
  'email_address_invalid',
  'signup_disabled',
];

type ChromeHeap = {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
};

function startPeriodicMemoryBreadcrumbs(): void {
  if (typeof window === 'undefined') return;
  const INTERVAL_MS = 60_000;
  setInterval(() => {
    try {
      const perf = performance as Performance & { memory?: ChromeHeap };
      const data: Record<string, unknown> = {
        platform: Capacitor.getPlatform(),
      };
      if (perf.memory) {
        data.usedJSHeapSize = perf.memory.usedJSHeapSize;
        data.totalJSHeapSize = perf.memory.totalJSHeapSize;
        data.jsHeapSizeLimit = perf.memory.jsHeapSizeLimit;
      } else {
        data.jsHeapProbe = 'unavailable';
      }
      Sentry.addBreadcrumb({
        category: 'memory.heap',
        type: 'debug',
        level: 'info',
        message: 'Periodic memory snapshot (StackedStreak-rq6)',
        data,
      });
    } catch {
      // never throw from background breadcrumb
    }
  }, INTERVAL_MS);
}

function extractAuthErrorCodeFromObject(obj: unknown, depth = 0): string | undefined {
  if (depth > 6 || obj == null || typeof obj !== 'object') return undefined;
  const r = obj as Record<string, unknown>;
  if (typeof r.code === 'string') return r.code;
  const nested = r.originalError ?? r.original_error;
  if (nested) return extractAuthErrorCodeFromObject(nested, depth + 1);
  return undefined;
}

function isAuthRetryableFetchEvent(
  event: { exception?: { values?: Array<{ value?: string; type?: string }> }; message?: string },
  hint: { originalException?: unknown } | undefined,
): boolean {
  // AuthRetryableFetchError = Supabase auth client failed to reach the
  // server (offline, CORS, request aborted, etc.). status:0 means the
  // fetch never produced a real HTTP response. Not a bug — the auth
  // path retries on its own. POKERBLITZ-T / -Z were ~33 events/wk of
  // these leaking as level=error.
  const firstEx = event.exception?.values?.[0];
  if (firstEx?.type === 'AuthRetryableFetchError') return true;
  const orig = hint?.originalException as { name?: string; status?: number } | undefined;
  if (orig?.name === 'AuthRetryableFetchError' && (orig.status === 0 || orig.status == null)) {
    return true;
  }
  const exVal = firstEx?.value ?? event.message ?? '';
  if (typeof exVal === 'string' && exVal.includes('AuthRetryableFetchError')) return true;
  return false;
}

function isExpectedAuthErrorEvent(
  event: { exception?: { values?: Array<{ value?: string; type?: string }> }; message?: string },
  hint: { originalException?: unknown } | undefined,
): boolean {
  const firstEx = event.exception?.values?.[0];
  if (firstEx?.type === 'AuthApiError') {
    const code = extractAuthErrorCodeFromObject(hint?.originalException);
    if (code && EXPECTED_AUTH_CODES.includes(code)) return true;
  }

  // Hint.originalException is the unwrapped error object — the most
  // reliable place to read .code from a Supabase AuthApiError.
  const orig = hint?.originalException as
    | { __isAuthError?: boolean; code?: string; status?: number; message?: string; originalError?: { __isAuthError?: boolean; code?: string } }
    | undefined;
  if (orig) {
    const directCode = orig.code;
    const nestedCode = orig.originalError?.code;
    const walked = extractAuthErrorCodeFromObject(hint?.originalException);
    if (directCode && EXPECTED_AUTH_CODES.includes(directCode)) return true;
    if (nestedCode && EXPECTED_AUTH_CODES.includes(nestedCode)) return true;
    if (walked && EXPECTED_AUTH_CODES.includes(walked)) return true;
    if (orig.__isAuthError && orig.status === 400 && orig.message?.includes('Invalid login credentials')) {
      return true;
    }
  }

  // Fall back to scanning the serialized exception value — the logger
  // sometimes captures these as string-bodied messages (incl. nested
  // `originalError` blobs — POKERBLITZ-A/G/C style).
  const exVal = firstEx?.value ?? event.message ?? '';
  if (typeof exVal === 'string') {
    for (const code of EXPECTED_AUTH_CODES) {
      if (
        exVal.includes(`"code":"${code}"`) ||
        exVal.includes(`'code':'${code}'`) ||
        exVal.includes(`code: '${code}'`)
      ) {
        return true;
      }
    }
    if (
      exVal.includes('"__isAuthError":true') &&
      exVal.includes('"status":400') &&
      (exVal.includes('Invalid login credentials') || exVal.includes('Email not confirmed'))
    ) {
      return true;
    }
    if (
      (exVal.includes('"name":"AuthApiError"') || exVal.includes('AuthApiError')) &&
      (exVal.includes('"status":400') || exVal.includes('"__isAuthError":true'))
    ) {
      for (const code of EXPECTED_AUTH_CODES) {
        if (exVal.includes(code)) return true;
      }
    }
    // POKERBLITZ-A / -8: invalid_credentials sometimes arrives serialized
    // with firstEx.type as bare Error (not AuthApiError) — the structured
    // gates above miss it. If any expected auth code appears in the value
    // AND the value mentions "credentials" or "Auth", treat it as expected.
    if (exVal.includes('credentials') || exVal.includes('AuthError') || exVal.includes('Auth')) {
      for (const code of EXPECTED_AUTH_CODES) {
        if (exVal.includes(code)) return true;
      }
    }
  }
  return false;
}

export function initSentry(): void {
  const variantKey = (() => {
    try {
      return getBrand().key;
    } catch {
      return 'unknown';
    }
  })();

  /**
   * DSN is per-variant (StackedStreak-7vip): Echidna builds were shipping
   * PokerBlitz's DSN, so Echidna web errors landed in the pokerblitz
   * Sentry project (POKERBLITZ-2D). Until an Echidna Sentry project
   * exists, VITE_SENTRY_DSN_ECHIDNA is unset and Echidna builds simply
   * don't init Sentry — the variant tag alone wasn't enough, because
   * cross-variant events still pollute PB's issue stream and release
   * health. Mirrors the Firebase-SDK gating pattern (Rule 4).
   */
  const activeDsn = variantKey === 'poker_blitz' ? dsn : echidnaDsn;
  if (!activeDsn || activeDsn.trim() === '' || activeDsn === 'YOUR_SENTRY_DSN_HERE') {
    if (variantKey === 'poker_blitz') {
      console.warn('Sentry: VITE_SENTRY_DSN not configured — error tracking disabled');
    } else {
      console.info(`Sentry: no DSN for variant "${variantKey}" — error tracking disabled`);
    }
    return;
  }

  const releasePrefix = variantKey === 'poker_blitz' ? 'pokerblitz' : variantKey;

  try {
    Sentry.init(
      {
        dsn: activeDsn,
        environment: import.meta.env.MODE ?? 'development',
        release: `${releasePrefix}@${import.meta.env.VITE_APP_VERSION ?? '1.7.46'}`,
        dist: Capacitor.getPlatform(),

        /**
         * Which GAME the error came from.
         *
         * Both variants ship from this repo into the SAME Sentry project, and
         * until now nothing distinguished them: an Echidna beta crash arrived
         * tagged `release: pokerblitz@1.7.x` and sat in the card game's issue
         * stream, indistinguishable from a real Poker Blitz regression. That
         * makes triage guesswork in both directions — you cannot tell whether
         * a spike is the live product breaking or the beta being poked at.
         *
         * A TAG rather than a different `release`: the release string is what
         * the build-time source-map upload keys on, so changing it here alone
         * would silently unsymbolicate every stack trace. Filter with
         * `variant:echidna` in Sentry.
         */
        initialScope: {
          tags: {
            variant: variantKey,
          },
        },

        // Capture unhandled promise rejections and JS errors
        integrations: [
          SentryReact.browserTracingIntegration(),
        ],

        // Performance: sample 20 % of transactions in production
        tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.2,

        // Don't capture breadcrumbs for console.log to reduce noise
        beforeBreadcrumb(breadcrumb) {
          if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
            return null;
          }
          return breadcrumb;
        },

        // Drop events that we know are expected user behavior, even if
        // some other code path logs them at error level. AuthContext.tsx
        // is the primary source — this is the safety net.
        // (Sentry issue 7447692148: invalid_credentials spam.)
        beforeSend(event, hint) {
          if (isExpectedAuthErrorEvent(event, hint)) return null;
          if (isAuthRetryableFetchEvent(event, hint)) return null;
          return event;
        },
      },
      // Required second arg: Sentry React init as the sibling SDK
      SentryReact.init,
    );

    // Expose on window so the existing errorTrackingSink picks it up
    // The sink interface requires { captureException, captureMessage }
    (window as unknown as Record<string, unknown>)['Sentry'] = {
      captureException: (
        error: Error,
        context: { level?: string; tags?: Record<string, unknown>; extra?: Record<string, unknown> },
        // The Sentry hint type went strict in recent SDK versions — the
        // runtime accepts our { level, tags, extra } shape but the type
        // declares those fields as never. Cast through unknown to keep
        // the public sink contract while quieting tsc.
      ) => Sentry.captureException(error, context as unknown as Parameters<typeof Sentry.captureException>[1]),
      captureMessage: (
        message: string,
        context: { level?: string; tags?: Record<string, unknown>; extra?: Record<string, unknown> },
      ) => Sentry.captureMessage(message, context as Parameters<typeof Sentry.captureMessage>[1]),
    };

    console.log(`Sentry: Initialized (env=${import.meta.env.MODE}, platform=${Capacitor.getPlatform()})`);
    startPeriodicMemoryBreadcrumbs();
  } catch (err) {
    // Never let Sentry init crash the app
    console.error('Sentry: Initialization failed', err);
  }
}

/**
 * Thin re-export of Sentry.addBreadcrumb so callers in render-critical
 * modules (e.g. src/app/page.tsx) don't have to import `@sentry/capacitor`
 * directly. Direct imports in heavy code-split chunks were pulling the
 * full SDK into those chunks (POKERBLITZ perf brief LOAD-03 / bd xek2).
 * Since this module is in the entry chunk via src/main.tsx, the SDK is
 * already loaded — and consumers get a tiny re-exported function instead
 * of a fresh dep edge.
 *
 * Safe to call before initSentry() runs: Sentry.addBreadcrumb is a no-op
 * on an uninitialized SDK rather than throwing.
 */
export function addBreadcrumb(breadcrumb: Parameters<typeof Sentry.addBreadcrumb>[0]): void {
  try {
    Sentry.addBreadcrumb(breadcrumb);
  } catch {
    // breadcrumbs are best-effort; never let them propagate
  }
}